
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { buildAuthRedirectUrl } from "@/utils/safeRedirect";

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { t } = useLanguage();
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  
  // Session inactivity timeout - 1 hour (in milliseconds)
  const SESSION_TIMEOUT = 60 * 60 * 1000; 
  
  useEffect(() => {
    if (!user) return;
    
    // Track user activity
    const updateActivity = () => {
      setLastActivity(Date.now());
      setIsSessionExpired(false);
    };
    
    // Add event listeners to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });
    
    // Set up inactivity checker
    const intervalId = setInterval(() => {
      const now = Date.now();
      if (now - lastActivity > SESSION_TIMEOUT) {
        setIsSessionExpired(true);
      }
    }, 60000); // Check every minute
    
    return () => {
      // Clean up event listeners
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
    };
  }, [user, lastActivity]);
  
  // Handle session expiry
  useEffect(() => {
    if (isSessionExpired && user) {
      // Force sign out if session expired due to inactivity
      const signOutDueToInactivity = async () => {
        try {
          await supabase.auth.signOut();
          // Use safe redirect to prevent open redirect vulnerability
          window.location.href = buildAuthRedirectUrl(location.pathname, true);
        } catch (error) {
          console.error("Error signing out due to inactivity:", error);
        }
      };
      
      signOutDueToInactivity();
    }
  }, [isSessionExpired, user, location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Save the current location to redirect back after login
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
